import React, { PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import { theme } from '../../../theme';
import { UIText } from '../../typography';
import { ClockIcon } from '../../../assets';
import { useBackgroundSize } from '../../../utils';

const Container = styled.div`
  display: flex;
  width: 367px;
  height: 78px;
  box-sizing: border-box;
  border: 1px solid ${theme.colors.neutrals.transparentGrays[7]};
  border-radius: 4px;
  box-shadow: 0px 4px 8px ${theme.colors.neutrals.transparentGrays[6]};
`;

const InnerContainerStyles = css`
  margin: ${theme.spacings[2].rem};
`;

const LeftContainer = styled.div`
  ${InnerContainerStyles}
  width: 78px;
`;

const ImageContainer = styled.div<{ imageUrl: string; backgroundSize: string }>`
  background-image: ${({ imageUrl }) => `url('${imageUrl}')`};
  background-position: center;
  background-size: ${({ backgroundSize }) => backgroundSize};
  background-repeat: no-repeat;
  height: 100%;
`;

const RightContainer = styled.div<{ verticalAlign: VerticalAlign }>`
  ${InnerContainerStyles};
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: ${({ verticalAlign }) =>
    verticalAlign === 'top' ? 'flex-start' : verticalAlign};
`;

const RightContainerUpper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${theme.spacings[1].rem};
`;

const Title = styled(UIText).attrs({ size: 'xs' })`
  max-width: 158px;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Time = styled(UIText).attrs({ size: 'xs' })`
  color: ${theme.colors.neutrals.solidGrays[3]};
`;

const StyledClockIcon = styled(ClockIcon).attrs({
  fill: theme.colors.neutrals.solidGrays[3],
  width: 12,
})`
  vertical-align: top;
`;

const RightContainerLower = styled.div`
  display: flex;
  justify-content: space-between;
`;

type VerticalAlign = 'top' | 'center';

export type CompactCardProps = {
  title: string;
  time?: string;
  imageUrl: string;
  bgSize?: string;
  imageHref?: string;
  onImageClick?: () => void;
  verticalAlign?: VerticalAlign;
};

export const CompactCard: React.FC<PropsWithChildren<CompactCardProps>> = ({
  title,
  time,
  imageUrl,
  imageHref,
  onImageClick,
  verticalAlign = 'top',
  children,
}) => {
  const getBackgroundSize = useBackgroundSize(imageUrl);

  return (
    <Container>
      <LeftContainer>
        <a href={imageHref}>
          <ImageContainer
            backgroundSize={getBackgroundSize}
            imageUrl={imageUrl}
            onClick={onImageClick}
          />
        </a>
      </LeftContainer>
      <RightContainer verticalAlign={verticalAlign}>
        <RightContainerUpper>
          <Title title={title}>{title}</Title>
          {time && (
            <Time>
              <StyledClockIcon /> {time}
            </Time>
          )}
        </RightContainerUpper>
        {children && <RightContainerLower>{children}</RightContainerLower>}
      </RightContainer>
    </Container>
  );
};
